import { Button } from 'components/ui/Button';
import { Divider } from 'components/ui/Divider';
import { BaseSection } from 'components/layout/Section/BaseSection';
import { NsKey } from 'enums/NsKey';
import { InputFactory } from 'factories/InputFactory';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddIcon } from 'components/icons/Icons';
import { Briefcase, User, UserCheck } from 'lucide-react';
import { BankAccountAddForm } from './BankAccountAddForm';
import { GenericMessage } from 'components/feedback/Message';
import { useFormikContext } from 'formik';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { InputConfig } from 'types/form/componentConfig';
import { BranchCode } from 'api/risk';
import { MandatePayer } from 'api/quote';
import { PaymentModeOption } from 'components/forms/PaymentModeOption';
import { generateMonthlyDropdown } from 'helpers/generateMonthlyDropdown';
import {
  useQuoteContractualizationData,
  useQuoteMandateActions,
  useQuoteMandateData
} from '../../store';
import { toDayInput } from 'core/utils/dates';
import {
  getPayerOption,
  quoteMandateInputsConfig,
  QuoteMandateFormFieldNames,
  QuoteMandateFormValues
} from '../../forms/quote-mandate';

// Carte « qui paie » de chaque payeur : titre, message sous les cartes et icône.
const PAYER_DISPLAY: Record<
  MandatePayer,
  { title: string; message: string; icon: React.ReactNode }
> = {
  [MandatePayer.Souscripteur]: {
    title: 'QUOTE_MANDATE_FORM.PAYER.SUBSCRIBER',
    message: 'MESSAGES.SUBSCRIBER_PAYS',
    icon: <User className="w-6 h-6" />
  },
  [MandatePayer.Beneficiaire]: {
    title: 'QUOTE_MANDATE_FORM.PAYER.BENEFICIARY',
    message: 'MESSAGES.BENEFICIARY_PAYS',
    icon: <UserCheck className="w-6 h-6" />
  },
  [MandatePayer.Courtier]: {
    title: 'QUOTE_MANDATE_FORM.PAYER.BROKER',
    message: 'MESSAGES.BROKER_PAYS',
    icon: <Briefcase className="w-6 h-6" />
  }
};

export const QuoteMandateLayout: React.FC = () => {
  const quoteData = useQuoteContractualizationData();
  const { t } = useTranslation(NsKey.Mandate);
  const [isMandateFormOpen, setMandateFormOpen] = useState<boolean>(false);
  const quoteMandate = useQuoteMandateData();
  const { values, setFieldValue } = useFormikContext<QuoteMandateFormValues>();
  const { fetchPayerBankAccounts } = useQuoteMandateActions();

  // Payeurs proposés par le back (souscripteur, bénéficiaire du dossier, courtier encaisseur).
  const payerOptions = useMemo(
    () => quoteMandate?.payerOptions ?? [],
    [quoteMandate]
  );
  const selectedPayerOption = useMemo(
    () => getPayerOption(quoteMandate, values.payer),
    [quoteMandate, values.payer]
  );

  // Le compte sélectionné n'a plus de sens quand le payeur change : il appartient à l'autre personne.
  const onPayerChange = useCallback(
    (payer: MandatePayer) => {
      if (payer === values.payer) return;
      setFieldValue(QuoteMandateFormFieldNames.Payer, payer);
      setFieldValue(QuoteMandateFormFieldNames.BankAccount, '');
    },
    [values.payer, setFieldValue]
  );

  const inputsConfig = useMemo(() => {
    const config = { ...quoteMandateInputsConfig }; // Créer une copie

    if (values.startDate && quoteMandate) {
      const startDateConfig = { ...(config.startDate as InputConfig) };

      const maxDateObj = new Date();
      maxDateObj.setMonth(maxDateObj.getMonth() + 6);

      // Dates calendaires : découpe de string / getters locaux, jamais toISOString
      startDateConfig.min = toDayInput(quoteMandate.startDate);
      startDateConfig.max = toDayInput(maxDateObj);
      startDateConfig.disabled = quoteData?.branchCode === BranchCode.RGI;

      config.startDate = startDateConfig;
    }

    return config;
  }, [values.startDate, quoteData?.branchCode, quoteMandate]);

  // Mémoïser renderField
  const renderField = useCallback(
    (fieldName: any) => {
      if (inputsConfig)
        return (
          <InputFactory
            config={inputsConfig}
            fieldName={fieldName}
            state={{ ...quoteMandate, quote: quoteData }}
          />
        );
    },
    [inputsConfig, quoteMandate, quoteData]
  );

  const monthlyDropdown = useMemo(() => generateMonthlyDropdown(), []);

  // Seul critère qui pilote le mandat : isGroupContract reste utilisé pour les
  // libellés et les règles d'affichage, mais pas pour décider du prélèvement.
  // Absent (back non redéployé) : on affiche, plutôt que de bloquer une signature.
  const requiresOwnMandate = quoteMandate?.requiresOwnMandate ?? true;

  // La lecture du mandat ne renvoie que les comptes du payeur enregistré : ceux du payeur
  // choisi sont rechargés à chaque changement.
  useEffect(() => {
    if (requiresOwnMandate && selectedPayerOption) {
      fetchPayerBankAccounts(selectedPayerOption);
    }
  }, [requiresOwnMandate, selectedPayerOption, fetchPayerBankAccounts]);

  const dueDateDropdown = useMemo(() => {
    const isDisabled = !!values.dueDate && !!quoteMandate?.isGroupContract;

    return (
      <FormikDropdown
        label={t('QUOTE_MANDATE_FORM.LABELS.END_DATE')}
        name={QuoteMandateFormFieldNames.DueDate}
        disabled={isDisabled}
        {...(isDisabled && values.dueDate !== null && { selectedItemKey: values.dueDate })}
        items={monthlyDropdown}
      />
    );
  }, [values.dueDate, quoteMandate?.isGroupContract, t, monthlyDropdown]);

  return (
    <div className="flex flex-col gap-4">
      <BaseSection title={t('QUOTE_MANDATE_FORM.LABELS.CONTRACT_SCHEDULING')}>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">
            {renderField(QuoteMandateFormFieldNames.StartDate)}
          </div>
          <div className="col-span-3">
            {renderField(QuoteMandateFormFieldNames.Frequency)}
          </div>
          <div className="col-span-3">{dueDateDropdown}</div>
        </div>
        <GenericMessage
          className="mt-4"
          type="info"
          dismissible={false}
          message={t('MESSAGES.MANDATE_FREQUENCY_INFOS')}
        />
      </BaseSection>
      {/*
        Le mandat SEPA se signe au niveau où le prélèvement part réellement. Un aliment
        de contrat cadre configuré en « chaque aliment règle son propre prélèvement » doit
        donc saisir son compte, alors que le seul critère « contrat collectif » le lui
        interdisait : ses échéances étaient créées, mais aucun prélèvement ne partait.
      */}
      {requiresOwnMandate && payerOptions.length > 1 && (
        <BaseSection title={t('QUOTE_MANDATE_FORM.LABELS.PAYER')}>
          <div className="flex flex-col gap-3">
            {payerOptions.map(option => (
              <PaymentModeOption
                key={option.code}
                icon={PAYER_DISPLAY[option.code].icon}
                title={t(PAYER_DISPLAY[option.code].title)}
                description={option.displayName ?? ''}
                selected={values.payer === option.code}
                onClick={() => onPayerChange(option.code)}
              />
            ))}
          </div>
          <GenericMessage
            className="mt-2"
            type="info"
            dismissible={false}
            message={t(PAYER_DISPLAY[values.payer].message)}
          />
        </BaseSection>
      )}
      {requiresOwnMandate && (
      <BaseSection
        title={t(
          values.payer === MandatePayer.Courtier
            ? 'QUOTE_MANDATE_FORM.LABELS.BROKER_DEBIT_ACCOUNT'
            : 'QUOTE_MANDATE_FORM.LABELS.DEBIT_ACCOUNT'
        )}
        titleAside={
          <Button
            color="secondary"
            round
            onClick={() => setMandateFormOpen(true)}
          >
            <AddIcon />
          </Button>
        }
      >
        <>
          <Divider className="my-4" />
          {renderField(QuoteMandateFormFieldNames.BankAccount)}
        </>
      </BaseSection>
      )}
      {requiresOwnMandate && isMandateFormOpen && selectedPayerOption && (
        <BankAccountAddForm
          open={isMandateFormOpen}
          onCancel={() => setMandateFormOpen(false)}
          payerOption={selectedPayerOption}
        />
      )}
      {/*
        Le bloc disparaît sans explication sinon : l'utilisateur qui connaît l'écran
        chercherait un compte à saisir là où il n'y en a plus à saisir.
      */}
      {!requiresOwnMandate && (
        <GenericMessage
          type="info"
          dismissible={false}
          message={t('MESSAGES.GROUP_CONTRACT_PAYS_FOR_ALL')}
        />
      )}
    </div>
  );
};
